/* eslint-disable @typescript-eslint/no-unused-vars */
import { ButtonProps, ButtonType, ButtonWidget, PopoverInstance, PopoverProps, PopoverWidget, StickerProps, StickerWidget } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';
import { assertUnreachable } from '../../utils/TypescriptHelpers';
import { LabelButtonContext } from '../LabelHelpers';
import { LabelPopoverStyle, LabelPopoverWidth } from '../Styles';

export type ChipTriggerElementProps = ({
  readonly kind: 'sticker-props';
} & Pick<StickerProps, 'icon' | 'stickerType'>);

export type ButtonTriggerElementProps = ({
  readonly kind: 'button-props';
} & Pick<ButtonProps, 'getContent' | 'style' | 'buttonType'>);

type TriggerElementProps = ChipTriggerElementProps | ButtonTriggerElementProps

function TriggerElement({ onClick, testId, title, ...props }: TriggerElementProps & { readonly testId: string, readonly onClick: () => void, readonly title?: React.ReactChild }): JSX.Element {
  switch (props.kind) {
    case 'sticker-props':
      return (
        <StickerWidget {...props} onClick={onClick} testId={testId}>{title || ''}</StickerWidget>
      )
    case 'button-props':
      return (
        <ButtonWidget {...props} buttonType={ButtonType.IconOutline} onClick={onClick} testId={testId} />
      )
  }
}

export type UsePopoverFetcher<TRenderProps, TFetcherParams> = (params: TFetcherParams) => Promise<TRenderProps>

export type UsePopoverContentProps<TRenderProps> = {
  readonly resolved?: TRenderProps;
  readonly loading?: boolean;
}

export type UsePopoverParams<TRenderProps> = {
  readonly popoverProps: Omit<PopoverProps, 'testId' | 'triggerElement' | 'popoverContent' | 'ref'>;
  readonly props: TRenderProps;
  readonly content: (props: UsePopoverContentProps<TRenderProps>) => JSX.Element;
  readonly controlKind: 'chip' | 'icon';
  readonly chipProps: (severe?: boolean) => TriggerElementProps;
  readonly iconProps: (severe?: boolean) => TriggerElementProps;
  readonly testId: string;
  readonly loading?: boolean;
  readonly onClick: () => void;
  readonly severe?: boolean;
}

export function useControlledPopover<TRenderProps extends { kind: 'dehydrated' | 'hydrated' }>(params: UsePopoverParams<TRenderProps>): JSX.Element | null {
  const {
    testId,
    controlKind,
    popoverProps,
    chipProps,
    iconProps,
    content: Content,
    loading,
    props,
    onClick,
    severe,
  } = params;
  const { hidePopoverWhenReferenceIsHidden } = React.useContext(LabelButtonContext);
  const controlTestId = `${testId}-controlled-popover`
  const popoverInstance = React.useRef<PopoverInstance>(null);

  const [triggerElementProps, setTriggerElementProps] = React.useState<TriggerElementProps | null>(null);
  const mounted = React.useRef(false);

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    switch (controlKind) {
      case 'icon':
        setTriggerElementProps(iconProps(severe));
        break;
      case 'chip':
        setTriggerElementProps(chipProps(severe));
        break;
      default:
        assertUnreachable(controlKind);
    }
  }, [chipProps, controlKind, iconProps, props.kind, severe]);

  const triggerElement = (triggerElementProps ? <TriggerElement testId={testId} title={popoverProps.title} onClick={onClick} {...triggerElementProps} /> : <></>);

  const popover = triggerElement ? (
    <PopoverWidget
      {...popoverProps}
      className={(hidePopoverWhenReferenceIsHidden ?? true) ? style(LabelPopoverStyle) : undefined}
      popoverContent={<Content resolved={props} loading={loading} />}
      triggerElement={triggerElement}
      ref={popoverInstance}
      testId={controlTestId}
      width={LabelPopoverWidth}
    />
  ) : null;

  return popover;
}
